/**
 * 影片觀看進度 Hook
 * 提供觀看進度的保存、恢復和管理功能
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import WatchProgressManager, { WatchProgress, LocalWatchProgress } from '../utils/watchProgressManager';

interface UseWatchProgressOptions {
  videoId: string;
  videoUrl: string;
  autoSave?: boolean; // 是否自動保存進度
  saveInterval?: number; // 保存間隔（毫秒）
  showProgressNotification?: boolean; // 是否顯示進度通知
  showProgressDialog?: boolean; // 是否顯示進度對話框（預設為 true）
}

interface UseWatchProgressReturn {
  currentProgress: WatchProgress | null;
  hasProgress: boolean;
  saveProgress: (currentTime: number, duration: number) => Promise<void>;
  restoreProgress: () => Promise<number | null>;
  clearProgress: () => void;
  isRestoring: boolean;
  progressPercentage: number;
  formattedProgress: string;
  shouldShowDialog: boolean; // 新增：是否應該顯示對話框
  closeDialog: () => void; // 新增：關閉對話框函數
}

export const useWatchProgress = (options: UseWatchProgressOptions): UseWatchProgressReturn => {
  const { user } = useAuth();
  const { videoId, videoUrl, autoSave = true, saveInterval = 10000, showProgressNotification = true, showProgressDialog = true } = options;
  
  const [currentProgress, setCurrentProgress] = useState<WatchProgress | null>(null);
  const [hasProgress, setHasProgress] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [formattedProgress, setFormattedProgress] = useState('');
  const [hasCheckedProgress, setHasCheckedProgress] = useState(false);
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  
  const lastSaveTime = useRef<number>(0);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  // 檢查是否有觀看進度（只在初始化時檢查一次）
  useEffect(() => {
    const checkProgress = async () => {
      if (!videoId || hasCheckedProgress) return;
      
      try {
        // 🔧 修復：使用數據驗證功能確保一致性
        const validation = await WatchProgressManager.validateProgressConsistency(user?.uid || '', videoId);
        
        if (validation.recommendedAction === 'use_local' && validation.localProgress) {
          setCurrentProgress({
            videoId,
            videoUrl,
            userId: user?.uid || '',
            ...validation.localProgress
          });
          setHasProgress(true);
          setProgressPercentage(Math.round(validation.localProgress.progress * 100));
          setFormattedProgress(WatchProgressManager.formatProgress(validation.localProgress.currentTime, validation.localProgress.duration));
          // 🔧 關鍵修復：只在 showProgressDialog 為 true 時才顯示對話框
          setShouldShowDialog(showProgressDialog);
          setHasCheckedProgress(true);
          return;
        }
        
        if (validation.recommendedAction === 'use_cloud' && validation.cloudProgress) {
          setCurrentProgress(validation.cloudProgress);
          setHasProgress(true);
          setProgressPercentage(Math.round(validation.cloudProgress.progress * 100));
          setFormattedProgress(WatchProgressManager.formatProgress(validation.cloudProgress.currentTime, validation.cloudProgress.duration));
          // 🔧 關鍵修復：只在 showProgressDialog 為 true 時才顯示對話框
          setShouldShowDialog(showProgressDialog);
          setHasCheckedProgress(true);
          
          // 同步到本地存儲
          WatchProgressManager.saveProgressLocally(videoId, validation.cloudProgress);
          return;
        }
        
        // 如果沒有推薦的進度數據，檢查是否有任何可用的進度
        if (validation.localProgress) {
          setCurrentProgress({
            videoId,
            videoUrl,
            userId: user?.uid || '',
            ...validation.localProgress
          });
          setHasProgress(true);
          setProgressPercentage(Math.round(validation.localProgress.progress * 100));
          setFormattedProgress(WatchProgressManager.formatProgress(validation.localProgress.currentTime, validation.localProgress.duration));
          // 🔧 關鍵修復：只在 showProgressDialog 為 true 時才顯示對話框
          setShouldShowDialog(showProgressDialog);
          setHasCheckedProgress(true);
          return;
        }
        
        setHasCheckedProgress(true);
      } catch (error) {
        console.error('❌ 檢查觀看進度失敗:', error);
        setHasCheckedProgress(true);
      }
    };
    
    checkProgress();
  }, [videoId, videoUrl, user?.uid, hasCheckedProgress, showProgressDialog]); // 🔧 添加 showProgressDialog 依賴

  // 保存觀看進度
  const saveProgress = useCallback(async (currentTime: number, duration: number) => {
    if (!videoId || !WatchProgressManager.shouldSaveProgress(currentTime, duration)) {
      return;
    }
    
    try {
      const progress = WatchProgressManager.calculateProgress(currentTime, duration);
      const isCompleted = WatchProgressManager.isCompleted(currentTime, duration);
      
      const progressData = {
        videoUrl,
        currentTime,
        duration,
        progress,
        lastWatched: new Date().toISOString(),
        watchCount: (currentProgress?.watchCount || 0) + 1,
        isCompleted
      };
      
      // 保存到本地存儲
      WatchProgressManager.saveProgressLocally(videoId, progressData);
      
      // 如果用戶已登入，保存到雲端
      if (user?.uid) {
        await WatchProgressManager.saveProgressToFirestore(user.uid, videoId, progressData);
      }
      
      // 更新狀態（但不觸發對話框，因為這是觀看過程中的保存）
      // 只更新進度數據，不設置 shouldShowDialog
      setCurrentProgress({
        videoId,
        userId: user?.uid || '',
        ...progressData
      });
      setHasProgress(true);
      setProgressPercentage(Math.round(progress * 100));
      setFormattedProgress(WatchProgressManager.formatProgress(currentTime, duration));
      
      if (showProgressNotification) {
        // console.log(`💾 保存觀看進度: ${WatchProgressManager.formatTime(currentTime)} / ${WatchProgressManager.formatTime(duration)}`);
      }
    } catch (error) {
      console.error('❌ 保存觀看進度失敗:', error);
    }
  }, [videoId, videoUrl, user?.uid, currentProgress?.watchCount, showProgressNotification]);

  // 恢復觀看進度
  const restoreProgress = useCallback(async (): Promise<number | null> => {
    if (!hasProgress || !currentProgress) {
      return null;
    }
    
    setIsRestoring(true);
    setShouldShowDialog(false); // 立即關閉對話框，避免重複觸發
    
    try {
      const restoreTime = currentProgress.currentTime;
      
      if (showProgressNotification) {
        console.log(`🔄 恢復觀看進度: ${WatchProgressManager.formatTime(restoreTime)}`);
      }
      
      // 更新觀看次數
      if (user?.uid) {
        await WatchProgressManager.updateWatchCount(user.uid, videoId);
      }
      
      return restoreTime;
    } catch (error) {
      console.error('❌ 恢復觀看進度失敗:', error);
      return null;
    } finally {
      setIsRestoring(false);
    }
  }, [hasProgress, currentProgress, user?.uid, videoId, showProgressNotification]);

  // 清除觀看進度
  const clearProgress = useCallback(() => {
    try {
      const localProgress = WatchProgressManager.getLocalProgress();
      delete localProgress[videoId];
      localStorage.setItem('watchProgress', JSON.stringify(localProgress));
      
      setCurrentProgress(null);
      setHasProgress(false);
      setProgressPercentage(0);
      setFormattedProgress('');
      setShouldShowDialog(false);
      
      console.log(`🗑️ 清除觀看進度: ${videoId}`);
    } catch (error) {
      console.error('❌ 清除觀看進度失敗:', error);
    }
  }, [videoId]);
  
  // 關閉對話框
  const closeDialog = useCallback(() => {
    setShouldShowDialog(false);
  }, []);

  // 自動保存進度
  useEffect(() => {
    if (!autoSave) return;
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [autoSave]);

  // 定期同步觀看進度
  useEffect(() => {
    if (!user?.uid) return;
    
    const syncInterval = setInterval(() => {
      WatchProgressManager.syncProgress(user.uid);
    }, 30000); // 30秒同步一次
    
    return () => clearInterval(syncInterval);
  }, [user?.uid]);

  return {
    currentProgress,
    hasProgress,
    saveProgress,
    restoreProgress,
    clearProgress,
    isRestoring,
    progressPercentage,
    formattedProgress,
    shouldShowDialog,
    closeDialog
  };
};

export default useWatchProgress;
